import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

/**
 * A tag pushed to the Google Tag Manager `dataLayer`.
 *
 * Per BetterSaver compliance/security rules, NEVER include PII (email,
 * name, IRD, balances, etc.). Only Firebase UIDs and non-identifying
 * event parameters are safe to push.
 */
export type GtmTag = {
  event: string;
  [key: string]: unknown;
};

type DataLayerWindow = {
  dataLayer?: GtmTag[];
} & Window;

/**
 * Thin wrapper around Google Tag Manager's `window.dataLayer`.
 *
 * The GTM container snippet lives in `index.html` so Google's tag
 * verification (Tag Assistant, "Send test traffic") can detect it from
 * the static HTML. The snippet itself is hostname-gated: it only fetches
 * `gtm.js` when `location.hostname === 'app.bettersaver.co.nz'`, so dev
 * and local navigation never reaches the shared GTM-NTNKBMS container.
 *
 * `dataLayer` is initialised unconditionally in `index.html`, so
 * `pushTag()` is safe in every environment — events accumulate in the
 * dataLayer array but only fire as tags when GTM is actually loaded.
 */
@Injectable({ providedIn: 'root' })
export class GtmService {
  private document = inject(DOCUMENT);

  /**
   * Push a tag onto the GTM `dataLayer`. Safe to call in any environment.
   * In dev/local the push is effectively a no-op because `gtm.js` is not
   * loaded, so no tags fire.
   */
  pushTag(tag: GtmTag): void {
    const win = this.document.defaultView as DataLayerWindow | null;
    if (!win) {
      return;
    }
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push(tag);
  }
}
